/**
 * ITLoadSection Component
 *
 * IT load configuration with 4 preset cards, peak load input,
 * load factor / diurnal swing sliders, 24h preview chart, and CSV upload.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Grid,
  TextField,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Slider,
} from '@mui/material';
import {
  Save as SaveIcon,
  UploadFile as UploadIcon,
  Memory as GPUIcon,
  Psychology as InferenceIcon,
  Shuffle as MixedIcon,
  Storage as TraditionalIcon,
} from '@mui/icons-material';
import { vortxColors, vortxTypography } from '@vortx/shared-ui';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const IT_PRESETS = [
  {
    key: 'gpu_training',
    label: 'GPU Training',
    icon: GPUIcon,
    description: 'High-density GPU clusters for ML/AI training',
    load_factor: 0.95,
    diurnal_swing: 0.05,
  },
  {
    key: 'inference',
    label: 'Inference',
    icon: InferenceIcon,
    description: 'Model serving with variable request load',
    load_factor: 0.70,
    diurnal_swing: 0.30,
  },
  {
    key: 'mixed',
    label: 'Mixed',
    icon: MixedIcon,
    description: 'Combined training, inference, and storage',
    load_factor: 0.80,
    diurnal_swing: 0.15,
  },
  {
    key: 'traditional',
    label: 'Traditional',
    icon: TraditionalIcon,
    description: 'Enterprise compute, storage, and networking',
    load_factor: 0.65,
    diurnal_swing: 0.20,
  },
];

const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    color: vortxColors.white,
    '& fieldset': { borderColor: vortxColors.teal },
    '&:hover fieldset': { borderColor: vortxColors.gold },
    '&.Mui-focused fieldset': { borderColor: vortxColors.gold, borderWidth: 2 },
  },
  '& .MuiInputLabel-root': {
    color: vortxColors.stone,
    '&.Mui-focused': { color: vortxColors.gold },
  },
  '& .MuiFormHelperText-root': {
    color: vortxColors.stone,
  },
};

/**
 * Generate a synthetic 24h IT load profile for preview.
 * Uses a sine wave centered on peak hours with configurable swing.
 */
function generatePreviewProfile(peakKw, loadFactor, diurnalSwing) {
  const peak = parseFloat(peakKw) || 1000;
  const baseLoad = peak * loadFactor;
  const swingAmplitude = peak * diurnalSwing;

  return Array.from({ length: 24 }, (_, hour) => {
    // Peak at 14:00 (2 PM), trough at 02:00 (2 AM)
    const phaseShift = -Math.PI / 2;
    const swing = swingAmplitude * Math.sin((2 * Math.PI * (hour - 2)) / 24 + phaseShift);
    const load = Math.max(0, baseLoad + swing);
    return { hour: `${hour}:00`, load_kw: Math.round(load) };
  });
}

function ITLoadSection({ project, onUpdate }) {
  const [formData, setFormData] = useState({
    it_preset: '',
    peak_it_load_kw: '',
    load_factor: 0.80,
    diurnal_swing: 0.15,
    csv_filename: '',
  });

  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (project) {
      setFormData({
        it_preset: project.it_preset || '',
        peak_it_load_kw: project.peak_it_load_kw || '',
        load_factor: project.load_factor ?? 0.80,
        diurnal_swing: project.diurnal_swing ?? 0.15,
        csv_filename: project.csv_filename || '',
      });
    }
  }, [project]);

  const previewData = useMemo(
    () => generatePreviewProfile(formData.peak_it_load_kw, formData.load_factor, formData.diurnal_swing),
    [formData.peak_it_load_kw, formData.load_factor, formData.diurnal_swing]
  );

  const handlePresetSelect = (preset) => {
    setFormData((prev) => ({
      ...prev,
      it_preset: preset.key,
      load_factor: preset.load_factor,
      diurnal_swing: preset.diurnal_swing,
    }));
    setSaveSuccess(false);
  };

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    setSaveSuccess(false);
  };

  const handleSliderChange = (field) => (_, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setSaveSuccess(false);
  };

  const handleCsvUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    // Store filename for display; actual upload handled by API on save
    setFormData((prev) => ({
      ...prev,
      csv_filename: file.name,
    }));
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(formData);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save IT load config:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          fontFamily: vortxTypography.heading,
          color: vortxColors.white,
          mb: 3,
        }}
      >
        IT Load Profile
      </Typography>

      {/* Preset Cards */}
      <Typography variant="subtitle2" sx={{ color: vortxColors.stone, mb: 2 }}>
        Select a workload preset or customize below
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {IT_PRESETS.map((preset) => {
          const IconComp = preset.icon;
          const isSelected = formData.it_preset === preset.key;
          return (
            <Grid item xs={12} sm={6} md={3} key={preset.key}>
              <Card
                onClick={() => handlePresetSelect(preset)}
                sx={{
                  cursor: 'pointer',
                  bgcolor: isSelected ? 'rgba(178, 140, 80, 0.15)' : vortxColors.charcoal,
                  border: `2px solid ${isSelected ? vortxColors.gold : vortxColors.teal}`,
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: vortxColors.gold,
                    boxShadow: `0 0 12px rgba(178, 140, 80, 0.2)`,
                  },
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <IconComp sx={{ fontSize: 36, color: isSelected ? vortxColors.gold : vortxColors.stone, mb: 1 }} />
                  <Typography variant="subtitle1" sx={{ color: vortxColors.white, fontWeight: 600 }}>
                    {preset.label}
                  </Typography>
                  <Typography variant="caption" sx={{ color: vortxColors.stone, display: 'block' }}>
                    {preset.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Peak Load + Sliders */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Peak IT Load"
            type="number"
            value={formData.peak_it_load_kw}
            onChange={handleChange('peak_it_load_kw')}
            helperText="Total IT power draw at peak (kW)"
            sx={textFieldSx}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography variant="body2" sx={{ color: vortxColors.stone, mb: 1 }}>
            Load Factor: {(formData.load_factor * 100).toFixed(0)}%
          </Typography>
          <Slider
            value={formData.load_factor}
            onChange={handleSliderChange('load_factor')}
            min={0.3}
            max={1.0}
            step={0.01}
            sx={{
              color: vortxColors.gold,
              '& .MuiSlider-thumb': { bgcolor: vortxColors.gold },
              '& .MuiSlider-track': { bgcolor: vortxColors.gold },
              '& .MuiSlider-rail': { bgcolor: vortxColors.teal },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography variant="body2" sx={{ color: vortxColors.stone, mb: 1 }}>
            Diurnal Swing: {(formData.diurnal_swing * 100).toFixed(0)}%
          </Typography>
          <Slider
            value={formData.diurnal_swing}
            onChange={handleSliderChange('diurnal_swing')}
            min={0}
            max={0.50}
            step={0.01}
            sx={{
              color: vortxColors.gold,
              '& .MuiSlider-thumb': { bgcolor: vortxColors.gold },
              '& .MuiSlider-track': { bgcolor: vortxColors.gold },
              '& .MuiSlider-rail': { bgcolor: vortxColors.teal },
            }}
          />
        </Grid>
      </Grid>

      {/* 24h Preview Chart */}
      <Typography variant="subtitle2" sx={{ color: vortxColors.stone, mb: 1 }}>
        24-Hour Load Preview
      </Typography>
      <Box sx={{ width: '100%', height: 250, mb: 4 }}>
        <ResponsiveContainer>
          <LineChart data={previewData}>
            <CartesianGrid strokeDasharray="3 3" stroke={vortxColors.teal} />
            <XAxis dataKey="hour" stroke={vortxColors.stone} tick={{ fontSize: 11 }} />
            <YAxis stroke={vortxColors.stone} tick={{ fontSize: 11 }} />
            <Tooltip
              contentStyle={{ backgroundColor: vortxColors.charcoal, border: `1px solid ${vortxColors.gold}` }}
              labelStyle={{ color: vortxColors.white }}
              itemStyle={{ color: vortxColors.gold }}
            />
            <Line
              type="monotone"
              dataKey="load_kw"
              stroke={vortxColors.gold}
              strokeWidth={2}
              dot={false}
              name="IT Load (kW)"
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>

      {/* CSV Upload */}
      <Box sx={{ mb: 3 }}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          style={{ display: 'none' }}
          onChange={handleCsvUpload}
        />
        <Button
          variant="outlined"
          startIcon={<UploadIcon />}
          onClick={() => fileInputRef.current?.click()}
          sx={{
            borderColor: vortxColors.teal,
            color: vortxColors.stone,
            '&:hover': {
              borderColor: vortxColors.gold,
              color: vortxColors.gold,
            },
          }}
        >
          Upload CSV Profile
        </Button>
        {formData.csv_filename && (
          <Typography variant="caption" sx={{ color: vortxColors.stone, ml: 2 }}>
            {formData.csv_filename}
          </Typography>
        )}
      </Box>

      {/* Save */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} sx={{ color: '#000' }} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{
            background: `linear-gradient(135deg, ${vortxColors.gold} 0%, #d4a574 100%)`,
            color: '#000',
            fontWeight: 600,
            '&:hover': {
              background: `linear-gradient(135deg, #d4a574 0%, ${vortxColors.gold} 100%)`,
            },
          }}
        >
          Save Changes
        </Button>
        {saveSuccess && <Alert severity="success">Changes saved successfully!</Alert>}
      </Box>
    </Box>
  );
}

export default ITLoadSection;
